import type { Dispatch, SetStateAction } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import type { FormikContextType } from 'formik'
import { useIntl } from 'react-intl'
import { type Id, toast } from 'react-toastify'

import { useAccessToken } from '@b-stock/fe-core/bstock-next/components'
import {
  isZeroQuotePlaceholder,
  useFlag,
} from '@b-stock/fe-core/bstock-next/helpers'
import type {
  OrchestratedAuctionListingDetails,
  OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'
import { BidStatus } from '@b-stock/fe-core/bstock-next/queries'
import { FormattedMessage, showSnackbar } from '@b-stock/fe-core/bstock-react'

import ToastContent from '@/buyer-portal/components/StyledToastContainer/ToastContent'
import useAccountId from '@/buyer-portal/helpers/useAccountId'
import { FeatureFlags } from '@/helpers/featureFlags'
import { orchestratedListingToGTMItemInput } from '@/helpers/telemetry/gtmItemAdapters'
import type { TrackToastView } from '@/helpers/telemetry/useTrackToastView'
import type { GTMEcommerceItem } from '@/helpers/types/gtmTypes'
import listingDetailsQuery from '@/queries/listingDetailsQuery'
import newSellerStorefrontsQuery from '@/queries/newSellerStorefrontsQuery'
import usePlaceBidMutation from '@/queries/usePlaceBidMutation'

import { BID_TRANSITION_DELAY } from '../../const'
import { type AuctionDetailState } from '../../useAuctionDetailState'
import { getContractAutoPayId, isContractListing } from '../../utils'
import { useSaveAutoPayMethod } from '../autoPayUtils'
import type { BidFormFormikValues } from '../BidFormFormikProvider'
import { useOnBidError } from './useOnBidError'

export const useOnBidConfirm = ({
  doNotify: doNotifyProp = true,
}: { doNotify?: boolean } = {}) => {
  const accountId = useAccountId()
  const accessToken = useAccessToken()
  const queryClient = useQueryClient()
  const bidMutation = usePlaceBidMutation()
  const onPlaceBidError = useOnBidError()
  const autoPayEnforced = useFlag(FeatureFlags.AUTOPAY_ENFORCED)
  const contractAutoPayEnabled = useFlag(FeatureFlags.ENABLE_CONTRACT_AUTOPAY)
  const isNewSellerTreatment =
    useFlag(FeatureFlags.NEW_SELLER_ALP_LABEL) === 'treatment'
  const { data: newSellerIds } = newSellerStorefrontsQuery.useWithOpts([], {
    enabled: isNewSellerTreatment,
  })
  const { formatMessage } = useIntl()
  const { saveAutoPayMethod } = useSaveAutoPayMethod()

  const onBidConfirm = async ({
    amount,
    auctionDetailState: { bidState },
    autoPayMethodId,
    bidLocation,
    buyerGroupId,
    isWireSelected,
    closeModal,
    formikContext,
    listing,
    selectedQuote,
    setIsPaused,
    trackToastView,
  }: {
    amount: number
    auctionDetailState: AuctionDetailState
    autoPayMethodId: string | null
    bidLocation: 'pdp' | 'list'
    /** First Dibs buyer group ID — passed when the FD window is active. */
    buyerGroupId?: string
    isWireSelected: boolean
    closeModal: () => void
    formikContext: FormikContextType<BidFormFormikValues>
    listing: OrchestratedAuctionListingDetails
    selectedQuote?: OrchestratedQuote | null
    setIsPaused: Dispatch<SetStateAction<boolean>>
    trackToastView?: TrackToastView
  }) => {
    const quickBidButtonNumber = formikContext.values.quickBidButtonNumber
    const { auction } = listing

    if (!auction._id) return

    const isShippingQuoteAvailable =
      !!selectedQuote && !isZeroQuotePlaceholder(selectedQuote)
    const isContract = isContractListing(listing)
    const contractAutoPayId = getContractAutoPayId(listing)
    const autoPayEnabledForListing =
      autoPayEnforced &&
      (!isContract || (contractAutoPayEnabled && !!contractAutoPayId))

    // FF autoPayEnforced is the master flag; contract AutoPay also requires
    // enable-contract-autopay so spot rollout can stay independent.
    // also skip when no valid shipping quote exists (can't calculate total).
    const paymentMethodId =
      autoPayEnabledForListing &&
      isShippingQuoteAvailable &&
      !listing.sellerCollects
        ? autoPayMethodId
        : null

    const doNotify =
      doNotifyProp &&
      // don't show toast messages for WON or LOST
      bidState !== BidStatus.WON &&
      bidState !== BidStatus.LOST

    try {
      formikContext.resetForm({ values: { amount: '' } })

      closeModal()

      let delay: Promise<boolean> = new Promise((resolve) => resolve(false))
      setIsPaused(true)

      let toastId: Id | null = null
      if (doNotify) {
        toastId = toast(
          <ToastContent
            title={
              <FormattedMessage id="BidConfirmationModal.toast.placingBid" />
            }
          >
            <FormattedMessage id="BidConfirmationModal.toast.placingBid.desc" />
          </ToastContent>,
          { closeButton: false }
        )
      }

      delay = new Promise((resolve) => {
        setTimeout(() => {
          setIsPaused(false)
          if (toastId) toast.dismiss(toastId)
          resolve(doNotify)
        }, BID_TRANSITION_DELAY)
      })

      // Build ecommerce item object for enhanced GTM tracking
      const itemInput = orchestratedListingToGTMItemInput(listing)
      const ecommerceItem: GTMEcommerceItem = {
        ...itemInput,
        price: amount,
        quantity: 1,
        index: 0,
      }

      const [{ data: response }, readyToNotify] = await Promise.all([
        bidMutation.mutateAsync({
          auctionId: auction._id,
          listingId: listing._id,
          amount,
          buyerGroupId,
          bidLocation,
          item: ecommerceItem,
          isNewSeller:
            isNewSellerTreatment &&
            (newSellerIds?.has(listing.storefrontId) ?? false),
          userId: accountId ?? undefined,
        }),
        delay,
      ])

      if (
        autoPayEnabledForListing &&
        isShippingQuoteAvailable &&
        !listing.sellerCollects &&
        (paymentMethodId || isWireSelected) &&
        accessToken
      ) {
        await saveAutoPayMethod({
          accessToken,
          accountId,
          isWireSelected,
          listing,
          paymentMethodId: paymentMethodId ?? undefined,
        })
      }

      if (readyToNotify) {
        if (response.winning) {
          showSnackbar({
            content: (
              <ToastContent
                title={
                  <FormattedMessage id="BidConfirmationModal.toast.winningBid" />
                }
              >
                <FormattedMessage id="BidConfirmationModal.toast.winningBid.desc" />
              </ToastContent>
            ),
            options: { autoClose: true, type: 'success' },
          })
          trackToastView?.(
            'Success',
            formatMessage({ id: 'BidConfirmationModal.toast.winningBid.desc' })
          )
        } else {
          showSnackbar({
            content: (
              <ToastContent
                title={
                  <FormattedMessage id="BidConfirmationModal.toast.losingBid" />
                }
              >
                <FormattedMessage id="BidConfirmationModal.toast.losingBid.desc" />
              </ToastContent>
            ),
            options: { autoClose: true, type: 'error' },
          })
          trackToastView?.(
            'Failure',
            formatMessage({ id: 'BidConfirmationModal.toast.losingBid.desc' }),
            { quick_bid_button_number: quickBidButtonNumber }
          )
        }
      }
    } catch (e) {
      void queryClient.invalidateQueries({
        queryKey: listingDetailsQuery.getKey(accessToken, [listing._id]),
      })
      formikContext.resetForm()
      const toastMessage = onPlaceBidError(e)
      trackToastView?.('Failure', toastMessage, {
        quick_bid_button_number: quickBidButtonNumber,
      })
    }
  }

  return onBidConfirm
}
